import { initI18n, loadLocaleResources, I18nextProvider, type SupportedLocale, type I18nInstance } from '@firmable/i18n';
import { useLocaleStore } from '@web/store/App/useLocaleStore';
import React from 'react';

let i18nInstance: I18nInstance | null = null;

function getOrCreateI18n(locale: SupportedLocale): I18nInstance {
  if (!i18nInstance) {
    i18nInstance = initI18n({ locale, namespaces: ['search', 'company', 'people', 'crm', 'subscription', 'list', 'settings', 'developer', 'dynamic-labels'], disableDetection: true });
  }
  return i18nInstance;
}

function LocaleProvider({ children }: { children: React.ReactNode }) {
  const locale = useLocaleStore((s) => s.locale);
  const i18n = getOrCreateI18n(locale);
  // Track whether the initial locale has loaded (only blocks first render for non-English)
  const [initialReady, setInitialReady] = React.useState(locale.startsWith('en'));

  React.useEffect(() => {
    if (locale.startsWith('en')) {
      if (i18n.language !== locale) i18n.changeLanguage(locale);
      setInitialReady(true);
      return;
    }

    let cancelled = false;
    // Don't set a loading state — keep showing the previous locale's UI
    // until the new locale chunk is loaded, then swap instantly.
    loadLocaleResources(i18n, locale).then(() => {
      if (cancelled) return;
      i18n.changeLanguage(locale);
      setInitialReady(true);
    });

    return () => { cancelled = true; };
  }, [locale, i18n]);

  // Only block render on the very first mount if we need to load a non-English locale.
  // After that, locale switches keep the previous UI visible until the new one is ready.
  if (!initialReady) return null;

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

export { LocaleProvider };
